import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calculator, Compass, Heart, Sparkles, ArrowRight, Check, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { servicesTranslations } from '../translations/services';
import { useSeo } from '../hooks/useSeo';

export const Services = () => {
  const { language } = useLanguage();
  const t = servicesTranslations[language];

  const isFr = language === 'fr';
  useSeo(
    isFr ? "Numérologie, couple & famille | Services à Ibiza et en visio" : "Comprendre qui l'on est - Stef Ruiz",
    isFr ? "Découvrez mes accompagnements en numérologie, couple, famille et connaissance de soi à Ibiza ou en visio partout dans le monde." : "A product of emergent.sh"
  );

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section with Ibiza Aerial View */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/ibiza-aerial.jpg)',
            backgroundPosition: '50% 50%',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/40 via-gray-900/30 to-gray-900/40"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-lg sm:text-xl text-white/95 leading-relaxed font-light mb-4">
            {t.hero.intro1}
          </p>
          <p className="text-base sm:text-lg text-white/90 leading-relaxed font-light">
            {t.hero.intro2}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            
            {/* Service 1: Thème numérologique complet - FEATURED */}
            <Card className="border-3 border-teal-300 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 bg-white relative">
              {/* Badge "Le plus demandé" - FUCHSIA */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                <div className="bg-gradient-to-r from-pink-600 to-fuchsia-600 text-white px-6 py-2 rounded-full shadow-lg flex items-center space-x-2" style={{background: 'linear-gradient(to right, #D94F8A, #C026D3)'}}>
                  <Star size={16} className="fill-current" />
                  <span className="text-sm font-semibold">{t.services.mostRequested}</span>
                </div>
              </div>
              
              <CardContent className="p-10 pt-12">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-pink-500 flex items-center justify-center mb-6 shadow-lg">
                  <Calculator size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-2">{t.services.numerology.title}</h3>
                <div className="flex items-center space-x-2 mb-4 text-teal-600 font-semibold">
                  <span className="text-xl">{t.services.numerology.price}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-base">{t.services.numerology.duration}</span>
                </div>
                
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t.services.numerology.description}
                </p>
                
                <p className="text-base text-gray-600 leading-relaxed mb-6 italic">
                  {t.services.numerology.benefit}
                </p>

                <Link to="/booking">
                  <Button className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white py-6 text-base">
                    {t.services.button}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Service 2: Conseils personnels */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-pink-500 flex items-center justify-center mb-6 shadow-lg">
                  <Compass size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-2">{t.services.coaching.title}</h3>
                <div className="flex items-center space-x-2 mb-4 text-teal-600 font-semibold">
                  <span className="text-xl">{t.services.coaching.price}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-base">{t.services.coaching.duration}</span>
                </div>
                
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t.services.coaching.description}
                </p>
                
                <p className="text-base text-gray-600 leading-relaxed mb-6 italic">
                  {t.services.coaching.benefit}
                </p>

                <Link to="/booking">
                  <Button className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white py-6 text-base">
                    {t.services.button}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Service 3: Accompagnement de couple et familial */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-pink-500 flex items-center justify-center mb-6 shadow-lg">
                  <Heart size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-2">{t.services.therapy.title}</h3>
                <div className="mb-4 space-y-1">
                  <div className="flex items-center space-x-2 text-teal-600 font-semibold">
                    <span className="text-lg">{t.services.therapy.priceIndividual}</span>
                    <span className="text-gray-400">•</span>
                    <span className="text-lg">{t.services.therapy.priceCouple}</span>
                  </div>
                  <p className="text-sm text-gray-500">{t.services.therapy.duration}</p>
                </div>
                
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t.services.therapy.description}
                </p>
                
                <p className="text-base text-gray-600 leading-relaxed mb-6 italic">
                  {t.services.therapy.benefit}
                </p>

                <Link to="/booking">
                  <Button className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white py-6 text-base">
                    {t.services.button}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Service 4: Accompagnement énergétique */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-pink-500 flex items-center justify-center mb-6 shadow-lg">
                  <Sparkles size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-2">{t.services.energy.title}</h3>
                <div className="flex items-center space-x-2 mb-4 text-teal-600 font-semibold">
                  <span className="text-xl">{t.services.energy.price}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-base">{t.services.energy.duration}</span>
                </div>
                
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t.services.energy.description}
                </p>
                
                <p className="text-base text-gray-600 leading-relaxed mb-6 italic">
                  {t.services.energy.benefit}
                </p>

                <Link to="/booking">
                  <Button className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white py-6 text-base">
                    {t.services.button}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pourquoi me consulter - Section de réassurance */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-pink-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-light text-teal-600 mb-4">
              {t.whyConsult.title}
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <Check size={20} className="text-teal-600" />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t.whyConsult.reason1}
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <Check size={20} className="text-teal-600" />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t.whyConsult.reason2}
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <Check size={20} className="text-teal-600" />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t.whyConsult.reason3}
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <Check size={20} className="text-teal-600" />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t.whyConsult.reason4}
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md sm:col-span-2 lg:col-span-2">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <Check size={20} className="text-teal-600" />
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t.whyConsult.reason5}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Témoignages */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center text-teal-600 mb-4">
            {t.testimonials.title}
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">{t.testimonials.subtitle}</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Témoignage 1 */}
            <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-4 text-yellow-500">
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  {t.testimonials.testimonial1.text}
                </p>
                <p className="text-gray-900 font-semibold">{t.testimonials.testimonial1.author}</p>
              </CardContent>
            </Card>
            
            {/* Témoignage 2 */}
            <Card className="border-none shadow-lg bg-gradient-to-br from-pink-50 to-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-4 text-yellow-500">
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  {t.testimonials.testimonial2.text}
                </p>
                <p className="text-gray-900 font-semibold">{t.testimonials.testimonial2.author}</p>
              </CardContent>
            </Card>
            
            {/* Témoignage 3 */}
            <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-4 text-yellow-500">
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                  <Star size={20} className="fill-current" />
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  {t.testimonials.testimonial3.text}
                </p>
                <p className="text-gray-900 font-semibold">{t.testimonials.testimonial3.author}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section - Amélioré */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-teal-600 mb-6 leading-tight">
            {t.cta.title}
          </h2>
          <p className="text-lg md:text-xl text-gray-700 mb-12 leading-relaxed font-light max-w-3xl mx-auto">
            {t.cta.subtitle}
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-lg px-12 py-8 h-auto shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              {t.cta.button}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
